// ویزیت تصویری — loading and driving the Jitsi IFrame API.
//
// React-free on purpose, like the rest of src/lib: this module knows about a
// script tag and an external API object, and nothing about the screen it is
// drawn on. The hook (src/hooks/useVideoVisit.js) owns the React side.
//
// ── Why the script is loaded at RUN TIME from the server's answer ────────────
// `external_api.js` must come from the SAME host that serves the conference,
// and which host that is arrives in `videoVisitSession.domain` — from the BFF,
// per environment. Bundling it, or configuring the URL in the client, would put
// a second copy of "which environment am I in" in the client, and a staging
// client pointed at a production media server is exactly the kind of mismatch
// runtimeEnv.js's BFF_WS_BASE derivation exists to prevent.

/** Resolved script loads, keyed by domain — the tag is added at most once. */
const loaded = new Map();

/**
 * Load `external_api.js` from one Jitsi host, once.
 *
 * Concurrent callers share one promise: the visit screen can mount, unmount and
 * remount while the script is still in flight, and three script tags for the
 * same file is a race over one global.
 */
export function loadJitsiScript(domain) {
  if (!domain) return Promise.reject(new Error('jitsi: no domain'));

  const existing = loaded.get(domain);
  if (existing) return existing;

  const promise = new Promise((resolve, reject) => {
    // Already there — a previous load in this session, or a remount after the
    // map was cleared. The global is what matters, not our bookkeeping.
    if (typeof window !== 'undefined' && window.JitsiMeetExternalAPI) {
      resolve(window.JitsiMeetExternalAPI);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://${domain}/external_api.js`;
    script.async = true;
    script.onload = () => {
      if (window.JitsiMeetExternalAPI) resolve(window.JitsiMeetExternalAPI);
      // A 200 that is not the script — a captive portal, a proxy error page.
      // Distinguished from a network failure because the fix is different.
      else reject(new Error('jitsi: script loaded but API is missing'));
    };
    script.onerror = () => {
      // The tag is dropped so a later attempt can retry: a failed load that
      // leaves its tag behind makes every retry a no-op.
      loaded.delete(domain);
      script.remove();
      reject(new Error('jitsi: script failed to load'));
    };
    document.head.appendChild(script);
  });

  loaded.set(domain, promise);
  return promise;
}

/**
 * The conference options both apps share.
 *
 * Anything that differs between the dentist and the patient is an argument;
 * everything below is true of a Parsina video visit wherever it is drawn, and
 * is spelled out here rather than left to the server's defaults so that reading
 * this file tells you what the call is.
 */
export function conferenceOptions({
  roomName, token, displayName, parentNode, prejoin = false, lang = 'fa',
  // The brand as the CALL shows it — window title, join/leave notifications.
  // An argument rather than a literal because it is user-facing and therefore
  // translatable; this module is React-free by design and has no i18n of its
  // own. The Latin default is the brand name, safe in any locale — until a
  // caller passes the translated one, the way the doctor app does from its
  // visit screen.
  providerName = 'Parsina',
}) {
  return {
    roomName,
    jwt: token,
    parentNode,
    width: '100%',
    height: '100%',
    userInfo: { displayName },
    configOverwrite: {
      // The dentist joins from inside the visit workspace and the patient from
      // a card they already tapped; a prejoin screen is a second confirmation
      // of a decision already made. The patient app turns it back on, where
      // checking your own camera before a doctor sees you is worth the tap.
      //
      // `prejoinConfig` and nothing else: the older `prejoinPageEnabled` is not
      // on the config whitelist — see «Branding» below — so it was doing
      // nothing, and this is the key the device-check screen actually obeys.
      prejoinConfig: { enabled: prejoin },
      startWithAudioMuted: false,
      startWithVideoMuted: false,
      disableDeepLinking: true,
      // 1:1. Nobody is invited, so nothing offers to invite.
      disableInviteFunctions: true,
      doNotStoreRoom: true,
      // ── Branding: the ONE key that reaches the conference ────────────────
      // An empty logo URL means the watermark renders nothing at all. It is not
      // a preference — it is the only lever the client has:
      //
      //   • Jitsi filters EVERY override we send. `configOverwrite` and
      //     `interfaceConfigOverwrite` both travel as URL-hash params that
      //     external_api.js appends, and the app picks them through a whitelist
      //     before merging. Whatever is not on the list is dropped in silence;
      //     the console logs only the survivors, as «Extending config with: …».
      //   • The interfaceConfig whitelist — 44 keys in jitsi/web:stable-11031 —
      //     contains NO logo or watermark key. SHOW_JITSI_WATERMARK,
      //     DEFAULT_LOGO_URL and their neighbours were set here for months and
      //     never once took effect: that is how the Jitsi logo outlived the
      //     commit that "removed" it.
      //   • The logo url moved to config.defaultLogoUrl. Left unset, a legacy
      //     shim in the bundle falls back to the hardcoded images/watermark.svg
      //     — the Jitsi logo — because the server's interface_config.js has
      //     DEFAULT_LOGO_URL commented out. Setting it is the whole fix.
      //   • This key is whitelisted only while the app runs in an IFRAME
      //     (`window.self !== window.top`), which every Parsina client does. If
      //     a future jitsi/web image drops that gate the logo comes back, and
      //     this line is the first place to look; the escape hatch is then
      //     server-side — see parsina.k8s.deployments/jitsi/README.md.
      //
      // To show پارسینا in the call rather than nothing, this is the one line
      // to change: 'images/parsina-logo.png' is already served by the media
      // server (parsina.k8s.deployments/jitsi/branding.yaml). Relative on
      // purpose — it resolves against the conference host, so the image is
      // same-origin, carries no build hash and names no environment.
      defaultLogoUrl: '',
      // Jitsi's OWN transcriber, refused. It is NOT «پایش بالینی», which is
      // Parsina's own AI reading the conversation live to draft the note and
      // writing nothing to disk. Do not turn this on to enable that — they are
      // unrelated systems. See docs/online-visit-audio-capture.md.
      transcribingEnabled: false,
      liveStreamingEnabled: false,
      hideConferenceSubject: true,
      hideConferenceTimer: false,
      disableThirdPartyRequests: true,
    },
    interfaceConfigOverwrite: {
      // Only whitelisted keys reach the conference (see «Branding» above), and
      // these are the ones the list actually honours. Anything about the
      // watermark or the logo belongs in configOverwrite.defaultLogoUrl —
      // adding a SHOW_*_WATERMARK key back here changes nothing at all.
      TOOLBAR_BUTTONS: [
        'microphone', 'camera', 'desktop', 'fullscreen',
        'settings', 'videoquality', 'tileview', 'hangup',
      ],
      // Nothing is KEPT. Recording is not offered because it is absent from
      // TOOLBAR_BUTTONS, the minted token refuses it, and there is no jibri
      // container that could honour it — those three facts are the guarantee.
      // The fileRecordingsEnabled flag that used to sit in configOverwrite read
      // like a fourth one and was discarded by the whitelist unread.
      PROVIDER_NAME: providerName,
      NATIVE_APP_NAME: providerName,
      SHOW_POWERED_BY: false,
      // No DEFAULT_BACKGROUND. It would have to be a literal colour — the
      // design tokens are CSS `var()` references and mean nothing inside
      // Jitsi's iframe — and a raw hex is exactly what the conformance tests
      // in both clients refuse. The branding is on OUR chrome around the frame;
      // the video area is the video.
      DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
    },
    lang,
  };
}

/**
 * Start a conference in `parentNode`.
 *
 * Returns the API object. The CALLER owns disposal — see the hook's teardown,
 * which detaches listeners before calling `dispose()` for the same reason
 * useServerSpeechToText does: a late event from a session being torn down must
 * not clear flags that a NEXT session has already set.
 */
export async function startConference(options) {
  const JitsiMeetExternalAPI = await loadJitsiScript(options.domain);
  const { domain, ...rest } = options;
  return new JitsiMeetExternalAPI(domain, conferenceOptions(rest));
}
